import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Modal } from 'reactstrap';

import axiosPrivate from 'config/axiosPrivate';

import { addUser, updateUser } from 'store/Admin/UserManagement';

import { CustomAutocomplete } from 'components/shared/DropDown/CustomAutocomplete';
import { MultiSelectPicker } from 'components/shared/DropDown/MultiSelectPicker';
import ErrorSuccesModal from 'components/shared/modal/ErrorSuccesModal';
import ConfirmationModal from 'components/shared/modal/ConfirmationModal';
import { classes } from 'components/shared/modal/modalClasses';

import { PWD_REGEX, EMAIL_REGEX, PHONENUMBER_REGEX } from 'utils/Regex';
import { PIXLINK_ROLES, ROLES } from 'utils/Constants';
import axiosPixLink from 'config/axiosPixLink';

const API = {
  pixopClients: '/user/pixOpsClient',
  createClient: '/user/create',
  updateClient: '/user/update',
  updateClientNotifications: (id) => `/user/superAdmin/client/${id}/notifications`,
};
const NOTIFICATION_LIST = [
  { key: 'mailNotificationActive', label: 'Désactiver les notifications' },
];

export const ClientModal = (props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [mail, setMail] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [accountsList, setAccountsList] = useState([]);

  const [errorSuccess, setErrorSuccess] = useState(false);
  const [errorSuccessMsg, setErrorSuccessMsg] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const [disabledForm, setDisabledForm] = useState(true);
  const [notifications, setNotifications] = useState({ mailNotificationActive: false });
  const [savedNotifications, setSavedNotifications] = useState({ mailNotificationActive: false });
  const [showNotifConfirmModal, setShowNotifConfirmModal] = useState(false);

  const notifChanged = NOTIFICATION_LIST.some(
    (n) => notifications[n.key] !== savedNotifications[n.key]
  );

  useEffect(() => {
    axiosPixLink
      .get(API.pixopClients)
      .then((res) => setAccountsList(res.data.map((x) => x.clientName)));
  }, []);

  useEffect(() => {
    if (props && props.user) {
      setFirstName(props.user.firstName);
      setLastName(props.user.lastName);
      setName(props.user.name);
      setMail(props.user.email);
      setPhoneNumber(props.user.phoneNumber);
      setAccounts(props.user.accounts || []);

      const initialNotifs = {
        mailNotificationActive:
          props.user.mailNotificationActive !== undefined
            ? !props.user.mailNotificationActive
            : false,
      };
      setNotifications(initialNotifs);
      setSavedNotifications(initialNotifs);
    }
  }, [props.user?.email]);

  const checkPasswordValid = useCallback(() => {
    return !password || PWD_REGEX.test(password);
  }, [password]);

  const checkValidForm = useCallback(() => {
    if (!mail || !phoneNumber || !firstName || !lastName || accounts.length === 0) {
      setDisabledForm(true);
      return;
    }

    if (!mail.match(EMAIL_REGEX)) {
      setDisabledForm(true);
      return;
    }

    return props.user
      ? setDisabledForm(!checkPasswordValid())
      : setDisabledForm(false);
  }, [
    accounts,
    checkPasswordValid,
    mail,
    firstName,
    lastName,
    name,
    phoneNumber,
    props.user,
  ]);

  useEffect(() => {
    checkValidForm();
  }, [
    mail,
    phoneNumber,
    password,
    name,
    firstName,
    lastName,
    accounts,
    checkValidForm,
  ]);

  const updateClient = () => {
    let body = {
      password: !password ? null : password,
      email: mail,
      firstName,
      lastName,
      phoneNumber,
      accounts,
      role: PIXLINK_ROLES.CLIENT,
    };

    const profileUpdate = axiosPixLink.put(API.updateClient, body, {
      params: { mail: props.user.email },
    });

    const requests = [profileUpdate];

    if (notifChanged) {
      const mailNotificationActive = !notifications.mailNotificationActive;
      requests.push(
        axiosPixLink.put(API.updateClientNotifications(props.user.id), {
          mailNotificationActive,
        })
      );
    }

    Promise.all(requests)
      .then(([profileRes]) => {
        if (notifChanged) {
          const mailNotificationActive = !notifications.mailNotificationActive;
          dispatch(updateUser({ ...profileRes.data, mailNotificationActive }));
          setSavedNotifications({ ...notifications });
        } else {
          dispatch(updateUser({ ...profileRes.data }));
        }
        setIsSuccess(true);
        setErrorSuccessMsg(
          `${t('Le client')} ${props.user.email} ${t(
            'a été mis à jour avec succès'
          )} !`
        );
        setErrorSuccess(true);
      })
      .catch((e) => {
        setIsSuccess(false);
        switch (e.response?.status) {
          case 409:
            setErrorSuccessMsg(
              `${t("Un utilisateur existe déjà avec l'email")} ${mail}`
            );
            break;
          default:
            setErrorSuccessMsg(t("Oups....! Une erreur s'est produite"));
            break;
        }
        setErrorSuccess(true);
      });
  };
  function handleAccountChange(newAccount) {
    if (
      newAccount !== null &&
      newAccount !== undefined &&
      !accounts?.includes(newAccount)
    ) {
      setAccounts(accounts ? [...accounts, newAccount] : [newAccount]);
    }
  }

  function removeAccount(accountToRemove) {
    setAccounts(accounts?.filter((a) => a !== accountToRemove));
  }
  const createClient = () => {
    let body = {
      email: mail,
      firstName,
      lastName,
      phoneNumber,
      accounts,
      role: PIXLINK_ROLES.CLIENT,
    };

    axiosPixLink
      .post(API.createClient, body)
      .then((res) => {
        dispatch(addUser({ ...res.data, role: PIXLINK_ROLES.CLIENT }));
        setIsSuccess(true);
        setErrorSuccessMsg(
          `${t('Le client')} ${mail} ${t('a été créé avec succès')}`
        );
        setErrorSuccess(true);
      })
      .catch((e) => {
        setIsSuccess(false);
        switch (e.response.status) {
          case 409:
            setErrorSuccessMsg(
              `${t("Un utilisateur existe déjà avec l'email")} ${mail}`
            );
            break;
          default:
            setErrorSuccessMsg(t("Oups....! Une erreur s'est produite"));
            break;
        }
        setErrorSuccess(true);
      });
  };
  const handleToggleNotification = (key) => {
    const newValue = !notifications[key];
    const newNotifications = { ...notifications, [key]: newValue };

    if (newValue === true && savedNotifications[key] === false) {
      setNotifications(newNotifications); // update l'état visuel
      setShowNotifConfirmModal(true);
    } else {
      setNotifications(newNotifications);
      submitNotificationsWith(newNotifications);
    }
  };
  const submitNotificationsWith = (notifState) => {
    const mailNotificationActive = !notifState.mailNotificationActive;

    axiosPixLink
      .put(API.updateClientNotifications(props.user.id), { mailNotificationActive })
      .then(() => {
        dispatch(updateUser({ ...props.user, mailNotificationActive }));
        setSavedNotifications({ ...notifState });
        setShowNotifConfirmModal(false);
        setIsSuccess(true);
        setErrorSuccessMsg(t('Vos préférences de notifications ont été mises à jour.'));
        setErrorSuccess(true);
      })
      .catch(() => {
        setNotifications({ ...savedNotifications });
        setShowNotifConfirmModal(false);
        setIsSuccess(false);
        setErrorSuccessMsg(t("Oups....! Une erreur s'est produite"));
        setErrorSuccess(true);
      });
  };
  const handleSaveNotifications = () => {
    const hasDeactivated = NOTIFICATION_LIST.some((n) => notifications[n.key]);
    if (hasDeactivated) {
      setShowNotifConfirmModal(true);
    } else {
      submitNotifications();
    }
  };

  const submitNotifications = () => {
    const mailNotificationActive = !notifications.mailNotificationActive;

    axiosPixLink
      .put(API.updateClientNotifications(props.user.id), {
        mailNotificationActive,
      })
      .then(() => {
        dispatch(updateUser({ ...props.user, mailNotificationActive }));
        setSavedNotifications({ ...notifications });
        setShowNotifConfirmModal(false);
        setIsSuccess(true);
        setErrorSuccessMsg(t('Vos préférences de notifications ont été mises à jour.'));
        setErrorSuccess(true);
      })
      .catch(() => {
        setShowNotifConfirmModal(false);
        setIsSuccess(false);
        setErrorSuccessMsg(t("Oups....! Une erreur s'est produite"));
        setErrorSuccess(true);
      });
  };

  if (props.user)
    return (
      <div>
        <Modal style={classes.modalCustom} isOpen={props.open}>
          <div style={classes.modalHeader} className="modal-header">
            <h5 style={classes.title} className="modal-title">
              {t('Modifier le client')}
            </h5>
            <button
              type="button"
              onClick={props.handleClose}
              style={classes.btnClose}
              className="btn-close"
            />
          </div>
          <div style={classes.modalPad} className="modal-body">
            <div className="row row-mb-lg">
              <div className="col-12 col-md-6">
                <div className="label-title"> {t('Nom')}</div>
                <div>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="form-control form-ct"
                  />
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="label-title">{t('Prénom')}</div>
                <div>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="form-control form-ct"
                  />
                </div>
              </div>
            </div>
            <div className="row row-mb-lg">
              <div className="col-12 col-md-12">
                <div className="label-title">{t('E - mail')}</div>
                <div>
                  <input
                    value={mail}
                    onChange={(e) => setMail(e.target.value)}
                    type="text"
                    className="form-control form-ct"
                  />
                </div>
              </div>
            </div>
            <div className="row row-mb-lg">
              <div className="col-12 col-md-6">
                <div className="label-title">{t('Téléphone')}</div>
                <div>
                  <input
                    value={phoneNumber}
                    onChange={(e) => {
                      if (
                        e.target.value === '' ||
                        PHONENUMBER_REGEX.test(e.target.value)
                      ) {
                        setPhoneNumber(e.target.value);
                      }
                    }}
                    type="text"
                    className="form-control form-ct"
                  />
                </div>
              </div>
            </div>
            <div className="row row-mb-lg">
              <div className="col-12 col-md-12">
                <div className="label-title">{t('Comptes clients')}</div>
                <MultiSelectPicker
                  options={accountsList}
                  selectedValues={accounts}
                  onSelect={handleAccountChange}
                />
                <div className="theme-list-container-filter">
                  {accounts.map((acc, index) => (
                    <span key={index} className="theme-container">
                      <span>{acc}</span>
                      <button type="button" onClick={() => removeAccount(acc)}>x</button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="row row-mb-lg">
              <div className="col-12 col-md-12">
                <div className="label-title">{t('Mot de passe')}*</div>
                <div className="input-group input-password">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="form-control"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <span
                    onClick={() => setShowPassword(!showPassword)}
                    className="input-group-text"
                  >
                    {showPassword ? (
                      <img
                        src="/Images/icon-eye-black.png"
                        alt="black eye icon"
                      />
                    ) : (
                      <img
                        src="/Images/icon-eye-gray.png"
                        alt="gray eye icon"
                      />
                    )}
                  </span>
                </div>
              </div>
              <div className="text-more mt-2">
                *
                {t(
                  'Avoir au moins 8 caractères avec une lettre majuscule, un chiffre, un symbole et ne pas être identique au nom.'
                )}
              </div>
            </div>


            <div className="row row-mb-lg">
              <div className="col-12">
                <div
                  style={{
                    padding: '1.2rem 0 0.2rem 0',
                    marginTop: '0.5rem',
                    borderTop: '1px solid #f0f0f0',
                  }}
                >
                  <h2 className="label-title" style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>
                    {t('Notifications par mail')}
                  </h2>

                  {NOTIFICATION_LIST.map((notif) => (
                    <div
                      key={notif.key}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                      }}
                    >
                      <span className="label-title" style={{ margin: 0, whiteSpace: 'nowrap' }}>
                        {t(notif.label)}
                      </span>
                      <div
                        onClick={() => handleToggleNotification(notif.key)}
                        style={{
                          cursor: 'pointer',
                          width: '48px',
                          height: '26px',
                          borderRadius: '13px',
                          backgroundColor: notifications[notif.key] ? '#ffe426' : '#ccc',
                          position: 'relative',
                          transition: 'background-color 0.2s',
                          flexShrink: 0,
                        }}
                      >
                        <span
                          style={{
                            position: 'absolute',
                            top: '3px',
                            left: notifications[notif.key] ? '25px' : '3px',
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            backgroundColor: '#fff',
                            transition: 'left 0.2s',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="box-button-bt">
              <div>
                {disabledForm ? (
                  <button className="bt-submit-disabled-y float-end">
                    {t('Sauvegarder')}
                  </button>
                ) : (
                  <button
                    className="bt-submit-y float-end"
                    onClick={updateClient}
                  >
                    {t('Sauvegarder')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </Modal>
        <ErrorSuccesModal
          open={errorSuccess}
          message={errorSuccessMsg}
          handleClose={() => {
            if (isSuccess === true) props.handleClose();
            setErrorSuccess(false);
          }}
          success={isSuccess}
          toggle={() => setErrorSuccess(!errorSuccess)}
        />
        <ConfirmationModal
          open={showNotifConfirmModal}
          message={t('Êtes-vous sûr(e) de ne pas vouloir recevoir ces notifications ?')}
          handleClose={() => {
            setShowNotifConfirmModal(false);
            setNotifications({ ...savedNotifications }); // rollback si annulé
          }}
          executeAction={() => submitNotificationsWith(notifications)}
        />
      </div>
    );

  return (
    <div>
      <Modal style={classes.modalCustom} isOpen={props.open}>
        <div style={classes.modalHeader} className="modal-header">
          <h5 style={classes.title} className="modal-title">
            {t('Ajouter un client')}
          </h5>
          <button
            type="button"
            onClick={props.handleClose}
            style={classes.btnClose}
            className="btn-close"
          />
        </div>
        <div style={classes.modalPad} className="modal-body">
          <div className="row row-mb-lg">
            <div className="col-12 col-md-6">
              <div className="label-title"> {t('Nom')}</div>
              <div>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="form-control form-ct"
                />
              </div>
            </div>
            <div className="col-12 col-md-6">
              <div className="label-title">{t('Prénom')}</div>
              <div>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="form-control form-ct"
                />
              </div>
            </div>
          </div>
          <div className="row row-mb-lg">
            <div className="col-12 col-md-12">
              <div className="label-title">{t('E-mail')}</div>
              <div>
                <input
                  value={mail}
                  onChange={(e) => setMail(e.target.value)}
                  type="text"
                  className="form-control form-ct"
                />
              </div>
            </div>
          </div>
          <div className="row row-mb-lg">
            <div className="col-12 col-md-6">
              <div className="label-title">{t('Téléphone')}</div>
              <div>
                <input
                  value={phoneNumber}
                  onChange={(e) => {
                    if (
                      e.target.value === '' ||
                      PHONENUMBER_REGEX.test(e.target.value)
                    ) {
                      setPhoneNumber(e.target.value);
                    }
                  }}
                  type="text"
                  className="form-control form-ct"
                />
              </div>
            </div>
          </div>
          <div className="row row-mb-lg">
            <div className="col-12 col-md-12">
              <div className="label-title">{t('Comptes clients')}</div>
              <MultiSelectPicker
                options={accountsList}
                selectedValues={accounts}
                onSelect={handleAccountChange}
              />
              <div className="theme-list-container-filter">
                {accounts.map((acc, index) => (
                  <span key={index} className="theme-container">
                    <span>{acc}</span>
                    <button type="button" onClick={() => removeAccount(acc)}>x</button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="box-button-bt">
            <div>
              {disabledForm ? (
                <button className="bt-submit-disabled-y float-end">
                  {t('Sauvegarder')}
                </button>
              ) : (
                <button
                  className="bt-submit-y float-end"
                  onClick={createClient}
                >
                  {t('Sauvegarder')}
                </button>
              )}
            </div>
          </div>
        </div>
      </Modal>
      <ErrorSuccesModal
        open={errorSuccess}
        message={errorSuccessMsg}
        handleClose={() => {
          if (isSuccess === true) props.handleClose();
          setErrorSuccess(false);
        }}
        success={isSuccess}
        toggle={() => setErrorSuccess(!errorSuccess)}
      />
    </div>
  );
};

export default ClientModal;
